import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import pessoasData from "../pessoas.json";
import { fetchSection, resolveImageSrc } from "../services/api";
import { renderMarkdown } from "../utils/markdown";
import "./Pessoas.css";

interface Pessoa {
  id: number;
  foto: string;
  nome: string;
  instituicao: string;
  email: string;
  lattes_url: string;
  bio: { pt: string; en: string; es: string };
  principais_producoes: { pt: string[]; en: string[]; es: string[] };
}

const Pessoas: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [pessoas, setPessoas] = useState<Pessoa[]>([]);
  const [selectedPerson, setSelectedPerson] = useState<Pessoa | null>(null);
  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showRightShadow, setShowRightShadow] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Fetch live content (falls back to the bundled copy), then randomize.
    let cancelled = false;
    (async () => {
      const data = await fetchSection<Pessoa[]>(
        "pessoas",
        pessoasData as Pessoa[]
      );
      if (cancelled) return;
      const shuffled = [...data].sort(() => Math.random() - 0.5);
      setPessoas(shuffled);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Update modal when language changes
  useEffect(() => {
    // Force re-render to update language-specific content
    if (selectedPerson) {
      setSelectedPerson(selectedPerson);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  useEffect(() => {
    // Check scroll position on mount and when pessoas change
    const checkScroll = () => {
      if (carouselRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
        // Add tolerance of 5px to handle rounding issues
        setShowLeftShadow(scrollLeft > 5);
        setShowRightShadow(scrollLeft < scrollWidth - clientWidth - 5);
      }
    };

    checkScroll();
    const carousel = carouselRef.current;
    if (carousel) {
      carousel.addEventListener("scroll", checkScroll);
      window.addEventListener("resize", checkScroll);
    }

    return () => {
      if (carousel) {
        carousel.removeEventListener("scroll", checkScroll);
        window.removeEventListener("resize", checkScroll);
      }
    };
  }, [pessoas]);

  const openPersonDetail = (pessoa: Pessoa) => {
    setSelectedPerson(pessoa);
  };

  const closePersonDetail = () => {
    setSelectedPerson(null);
  };

  // Map i18n language codes to data structure language codes
  const getCurrentLang = (): "pt" | "en" | "es" => {
    const lang = i18n.language;
    if (lang === "en-US") return "en";
    if (lang === "es-ES") return "es";
    return "pt"; // default to pt-BR
  };

  const scroll = (direction: "left" | "right") => {
    if (carouselRef.current) {
      const scrollAmount = 300;
      const newScrollLeft =
        carouselRef.current.scrollLeft +
        (direction === "right" ? scrollAmount : -scrollAmount);
      carouselRef.current.scrollTo({
        left: newScrollLeft,
        behavior: "smooth",
      });
    }
  };

  return (
    <section id="people" className="pessoas-section">
      <div className="pessoas-container">
        <h1 className="pessoas-main-title">{t("header.nav.people")}</h1>

        {/* Custom Scrollable Carousel */}
        <div className="carousel-wrapper">
          <button
            className="carousel-arrow carousel-arrow-left"
            onClick={() => scroll("left")}
            aria-label="Scroll left"
          >
            ‹
          </button>

          <div
            className={`carousel-scroll-wrapper ${
              showLeftShadow ? "show-left-shadow" : ""
            } ${showRightShadow ? "show-right-shadow" : ""}`}
          >
            <div className="carousel-container" ref={carouselRef}>
              {pessoas.map((pessoa) => (
                <div
                  key={pessoa.id}
                  className="pessoa-card"
                  onClick={() => openPersonDetail(pessoa)}
                >
                  <div className="pessoa-image-container">
                    <img
                      src={resolveImageSrc(pessoa.foto)}
                      alt={pessoa.nome}
                      className="pessoa-image"
                    />
                  </div>
                  <div className="pessoa-info">
                    <div className="pessoa-name">{pessoa.nome}</div>
                    <div className="pessoa-institution">
                      {pessoa.instituicao}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button
            className="carousel-arrow carousel-arrow-right"
            onClick={() => scroll("right")}
            aria-label="Scroll right"
          >
            ›
          </button>
        </div>
      </div>

      {/* Person Detail Modal */}
      {selectedPerson && (
        <div className="person-modal-overlay" onClick={closePersonDetail}>
          <div className="person-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closePersonDetail}>
              ×
            </button>

            <div className="modal-content">
              <div className="modal-header">
                <img
                  src={resolveImageSrc(selectedPerson.foto)}
                  alt={selectedPerson.nome}
                  className="modal-image"
                />
                <div className="modal-info">
                  <h3 className="modal-name">{selectedPerson.nome}</h3>
                  <p className="modal-institution">
                    {selectedPerson.instituicao}
                  </p>
                  <div className="modal-links">
                    <p className="modal-email">
                      <a href={`mailto:${selectedPerson.email}`}>
                        {selectedPerson.email}
                      </a>
                    </p>
                    {selectedPerson.lattes_url && (
                      <p className="modal-lattes">
                        <a
                          href={selectedPerson.lattes_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t("pessoas.lattesLink")}
                        </a>
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="modal-bio">
                <div className="section-description">
                  {renderMarkdown(selectedPerson.bio[getCurrentLang()])}
                </div>
              </div>

              {selectedPerson.principais_producoes[getCurrentLang()].length >
                0 && (
                <div className="modal-productions">
                  <h4>{t("pessoas.mainProductions")}</h4>
                  <ul>
                    {selectedPerson.principais_producoes[getCurrentLang()].map(
                      (producao, index) => (
                        <li key={index}>{producao}</li>
                      )
                    )}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Pessoas;
